import { motion } from 'framer-motion';
import { useEffect } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';
import FloatingButtons from '../home/components/FloatingButtons';
import { useInView } from '../home/hooks/useInView';

const Blagnac = () => {
  const [heroRef, heroInView] = useInView({ threshold: 0.1 });
  const [servicesRef, servicesInView] = useInView({ threshold: 0.1 });
  const [testimonialsRef, testimonialsInView] = useInView({ threshold: 0.1 });
  const [contentRef, contentInView] = useInView({ threshold: 0.1 });

  useEffect(() => {
    document.title = 'Réparation de fenêtres à Blagnac – Intervention rapide 24h/24 | Vitrerie 31';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Spécialiste réparation fenêtres à Blagnac (31700). Intervention sous 2h près de l\'aéroport. Fenêtres PVC, bois, alu. ☎ 05 54 54 25 45');
    }
  }, []);

  const services = [
    { icon: 'ri-tools-line', title: 'Réparation Fenêtres PVC', desc: 'Joints, mécanismes, vitrage' },
    { icon: 'ri-hammer-line', title: 'Fenêtres Bois', desc: 'Rénovation et entretien' },
    { icon: 'ri-window-line', title: 'Fenêtres Aluminium', desc: 'Réparation et remplacement' },
    { icon: 'ri-shield-check-line', title: 'Vitrerie d\'urgence', desc: 'Intervention 24h/24' }
  ];

  const testimonials = [
    {
      name: 'Laurent Mercier',
      location: 'Quartier Odyssud',
      text: 'Fenêtre endommagée par les vibrations de l\'aéroport. Réparation rapide et efficace. Très satisfait du résultat !',
      rating: 5
    },
    {
      name: 'Isabelle Rousseau',
      location: 'Centre-ville Blagnac',
      text: 'Problème d\'isolation phonique résolu. L\'équipe a été très professionnelle et à l\'écoute de mes besoins.',
      rating: 5
    },
    {
      name: 'Thomas Blanc',
      location: 'Quartier Andromède',
      text: 'Remplacement de vitrage en urgence. Intervention le jour même, travail impeccable. Je recommande !',
      rating: 5
    }
  ];

  const problemesTypiques = [
    { icon: 'ri-volume-vibrate-line', title: 'Nuisances sonores', desc: 'Isolation phonique renforcée' },
    { icon: 'ri-shake-hands-line', title: 'Vibrations aéroport', desc: 'Joints et fixations fragilisés' },
    { icon: 'ri-water-flash-line', title: 'Condensation', desc: 'Humidité et double vitrage' },
    { icon: 'ri-lock-unlock-line', title: 'Mécanismes usés', desc: 'Réparation et remplacement' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section ref={heroRef} className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50 via-white to-orange-50"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-transparent"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="ri-map-pin-line"></i>
              <span>Blagnac - Haute-Garonne (31700)</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Réparation de fenêtres à <strong className="text-teal-600">Blagnac</strong>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Expert en isolation phonique et réparation de fenêtres près de l'aéroport
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <a
                href="tel:0554542545"
                className="inline-flex items-center gap-2 bg-teal-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-teal-700 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer active:scale-95"
              >
                <i className="ri-phone-line text-xl"></i>
                05 54 54 25 45
              </a>
              <a
                href="#contact"
                className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-50 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer border-2 border-teal-600 active:scale-95"
              >
                <i className="ri-message-line text-xl"></i>
                Devis gratuit
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-time-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Intervention rapide</h3>
                <p className="text-gray-600 text-sm">Sous 2h à Blagnac</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-plane-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Près de l'aéroport</h3>
                <p className="text-gray-600 text-sm">Isolation phonique renforcée</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-24-hours-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Urgences 24h/24</h3>
                <p className="text-gray-600 text-sm">7 jours sur 7</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section ref={servicesRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={servicesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nos services à <strong className="text-teal-600">Blagnac</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Solutions adaptées aux contraintes de proximité de l'aéroport Toulouse-Blagnac
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={servicesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-teal-50 to-orange-50 rounded-lg p-6 hover:shadow-xl transition-all cursor-pointer group"
              >
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mb-4 group-hover:bg-teal-500 transition-colors">
                  <i className={`${service.icon} text-3xl text-teal-600 group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                <p className="text-gray-600">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Problèmes Typiques */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Problèmes spécifiques à <strong className="text-teal-600">Blagnac</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              La proximité de l'aéroport crée des contraintes particulières pour vos fenêtres
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {problemesTypiques.map((probleme, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-6 shadow-lg hover:shadow-xl transition-all"
              >
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                  <i className={`${probleme.icon} text-2xl text-orange-600`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{probleme.title}</h3>
                <p className="text-gray-600 text-sm">{probleme.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-gradient-to-r from-teal-500 to-orange-500 rounded-lg p-8 text-white text-center">
            <h3 className="text-2xl font-bold mb-4">Blagnac : des contraintes uniques</h3>
            <p className="text-lg mb-6 opacity-90 max-w-3xl mx-auto">
              Située à <strong>7 km au nord-ouest de Toulouse</strong>, Blagnac abrite l'aéroport international Toulouse-Blagnac. Les <strong>nuisances sonores et vibrations</strong> des avions fragilisent les fenêtres, particulièrement dans les quartiers <strong>Odyssud, Andromède et le Centre-ville</strong>. Nous proposons des solutions d'isolation phonique renforcée adaptées à votre environnement.
            </p>
          </div>
        </div>
      </section>

      {/* Témoignages Clients */}
      <section ref={testimonialsRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={testimonialsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Avis de nos clients à <strong className="text-teal-600">Blagnac</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Ils nous ont fait confiance pour leurs fenêtres
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={testimonialsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-teal-50 to-orange-50 rounded-lg p-6 shadow-lg"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-500 text-xl"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                <div className="border-t border-gray-200 pt-4">
                  <p className="font-bold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-600 flex items-center gap-1">
                    <i className="ri-map-pin-line"></i>
                    {testimonial.location}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* NOUVEAU BLOC 1 - Isolation Phonique Aéroport */}
      <section ref={contentRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={contentInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="relative"
            >
              <div className="relative w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/58758c9243609f62f87f2b3059cfa40a.jpg"
                  alt="Isolation phonique fenêtres Blagnac aéroport"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-teal-900/60 via-transparent to-transparent"></div>
                
                <div className="absolute bottom-6 left-6 bg-white rounded-xl shadow-xl p-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                      <i className="ri-volume-mute-line text-teal-600 text-2xl"></i>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-gray-900">Anti-Bruit</p>
                      <p className="text-sm text-gray-600">Expert acoustique</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={contentInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                <strong className="text-teal-600">Isolation Phonique Spéciale Aéroport</strong> à Blagnac
              </h3>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Située à proximité immédiate de l'<strong>aéroport Toulouse-Blagnac</strong>, la ville subit des nuisances sonores importantes liées au trafic aérien. Notre expertise en <strong>isolation phonique de fenêtres à Blagnac</strong> vous permet de retrouver le calme et la tranquillité dans votre habitation, même sous les couloirs aériens.
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Nous installons des <strong>fenêtres anti-bruit haute performance</strong> avec vitrage acoustique spécial, capables de réduire les nuisances sonores jusqu'à 45 décibels. Nos solutions incluent le <strong>double vitrage asymétrique acoustique</strong>, le <strong>triple vitrage phonique</strong> et les <strong>joints d'étanchéité acoustiques renforcés</strong> pour une isolation optimale à Blagnac.
              </p>

              <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-xl p-6 mb-6">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-sound-module-line text-teal-600 mr-2 text-xl"></i>
                  Performance Acoustique Garantie
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-teal-600 mb-1">-45dB</p>
                    <p className="text-sm text-gray-600">Réduction bruit avion</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-orange-600 mb-1">-38dB</p>
                    <p className="text-sm text-gray-600">Réduction bruit route</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-flight-takeoff-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Vitrage Acoustique Spécial Aéroport</h4>
                    <p className="text-gray-600 text-sm">Double vitrage asymétrique 10/16/6 avec gaz argon pour atténuer les basses fréquences</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-shield-check-line text-2xl text-orange-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Éligible Aides Aéroport</h4>
                    <p className="text-gray-600 text-sm">Subventions spécifiques pour riverains de l'aéroport - Nous gérons vos dossiers</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-home-smile-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Confort Acoustique Total</h4>
                    <p className="text-gray-600 text-sm">Sommeil paisible, concentration améliorée, qualité de vie retrouvée</p>
                  </div>
                </div>
              </div>

              <a
                href="#contact"
                className="inline-block bg-gradient-to-r from-teal-500 to-teal-600 text-white px-8 py-4 rounded-full font-semibold hover:from-teal-600 hover:to-teal-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i>
                Devis Isolation Phonique
              </a>
            </motion.div>
          </div>

          {/* NOUVEAU BLOC 2 - Sécurité Renforcée */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={contentInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="order-2 lg:order-1"
            >
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                <strong className="text-orange-600">Fenêtres Anti-Effraction</strong> à Blagnac
              </h3>
              <p className="text-gray-600 mb-4 leading-relaxed">
                La sécurité de votre domicile est primordiale. Nous proposons des solutions de <strong>fenêtres anti-effraction à Blagnac</strong> pour protéger efficacement votre habitation contre les tentatives d'intrusion. Nos <strong>fenêtres sécurisées</strong> répondent aux normes les plus strictes et sont certifiées pour résister aux effractions.
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Nos installations incluent le <strong>vitrage feuilleté anti-effraction</strong>, les <strong>serrures multipoints haute sécurité</strong>, les <strong>gâches renforcées</strong> et les <strong>vitrages retardateurs d'effraction classe P4A à P8B</strong>. Nous équipons également vos fenêtres de <strong>poignées à clé</strong> et de <strong>systèmes de verrouillage automatique</strong> pour une protection maximale à Blagnac.
              </p>

              <div className="bg-orange-50 border-l-4 border-orange-600 rounded-lg p-6 mb-6">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-shield-star-line text-orange-600 mr-2 text-xl"></i>
                  Certifications Sécurité
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center text-gray-700">
                    <i className="ri-check-line text-orange-600 mr-3"></i>
                    <span className="text-sm"><strong>Norme A2P</strong> : Certification CNPP pour serrures</span>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <i className="ri-check-line text-orange-600 mr-3"></i>
                    <span className="text-sm"><strong>Classe RC2</strong> : Résistance effraction 3 minutes</span>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <i className="ri-check-line text-orange-600 mr-3"></i>
                    <span className="text-sm"><strong>Vitrage P4A</strong> : Retardateur d'effraction certifié</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-lock-2-line text-2xl text-orange-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Serrures Multipoints Haute Sécurité</h4>
                    <p className="text-gray-600 text-sm">3 à 5 points de verrouillage avec cylindre anti-perçage et anti-crochetage</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-window-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Vitrage Feuilleté Retardateur</h4>
                    <p className="text-gray-600 text-sm">Film PVB multicouche qui maintient le verre en place même après impact</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-percent-line text-2xl text-orange-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Réduction Prime Assurance</h4>
                    <p className="text-gray-600 text-sm">Jusqu'à 20% de réduction sur votre assurance habitation avec nos fenêtres certifiées</p>
                  </div>
                </div>
              </div>

              <a
                href="#contact"
                className="inline-block bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-full font-semibold hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-shield-check-line mr-2"></i>
                Devis Sécurité Gratuit
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={contentInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="relative order-1 lg:order-2"
            >
              <div className="relative w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=high%20security%20anti%20burglary%20window%20with%20reinforced%20laminated%20glass%20multi%20point%20locking%20system%20showing%20detailed%20view%20of%20secure%20hardware%20mechanisms%20safety%20features%20burglar%20resistant%20technology%20professional%20installation%20in%20modern%20protected%20home%20simple%20clean%20background&width=800&height=600&seq=blagnac-security-window-v1&orientation=landscape"
                  alt="Fenêtres anti-effraction sécurisées Blagnac"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-orange-900/60 via-transparent to-transparent"></div>
                
                <div className="absolute bottom-6 right-6 bg-white rounded-xl shadow-xl p-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                      <i className="ri-shield-check-line text-orange-600 text-2xl"></i>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-gray-900">Sécurité</p>
                      <p className="text-sm text-gray-600">Certifié RC2</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quartiers desservis */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Tous les quartiers de <strong className="text-teal-600">Blagnac</strong>
            </h2>
            <p className="text-lg text-gray-600">Intervention rapide dans toute la commune</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {['Centre-ville', 'Odyssud', 'Andromède', 'Grand Noble', 'Grenade', 'Constellation', 'Ritouret', 'Barradels'].map((quartier, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-4 shadow-md hover:shadow-lg transition-all text-center"
              >
                <i className="ri-map-pin-2-fill text-2xl text-teal-600 mb-2"></i>
                <p className="font-semibold text-gray-900">{quartier}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-teal-500 to-orange-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Besoin d'une réparation de fenêtre à Blagnac ?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Intervention rapide sous 2h - Devis gratuit - Urgences 24h/24
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:0554542545"
              className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-50 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer active:scale-95"
            >
              <i className="ri-phone-line text-xl"></i>
              Appelez maintenant
            </a>
            <a
              href="/#contact"
              className="inline-flex items-center gap-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/10 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer active:scale-95"
            >
              <i className="ri-message-line text-xl"></i>
              Demander un devis
            </a>
          </div>
        </div>
      </section>

      {/* Liens vers autres villes */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Nous intervenons aussi dans les villes voisines
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Toulouse</a>
            <span className="text-gray-400">•</span>
            <a href="/colomiers" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Colomiers</a>
            <span className="text-gray-400">•</span>
            <a href="/tournefeuille" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Tournefeuille</a>
            <span className="text-gray-400">•</span>
            <a href="/muret" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Muret</a>
            <span className="text-gray-400">•</span>
            <a href="/balma" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Balma</a>
            <span className="text-gray-400">•</span>
            <a href="/ramonville" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Ramonville</a>
            <span className="text-gray-400">•</span>
            <a href="/cugnaux" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Cugnaux</a>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default Blagnac;
